/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import * as Sentry from '@sentry/react';
import { FertilityBar } from './components/FertilityBar';
import { HabitCard } from './components/HabitCard';
import { LockedHabitCard } from './components/LockedHabitCard';
import { ProgressChart } from './components/ProgressChart';
import { DailyQuestionCard } from './components/DailyQuestionCard';
import { HabitDetailModal } from './components/HabitDetailModal';
import { SubscriptionCard } from './components/SubscriptionCard';
import { AccountCard } from './components/AccountCard';
import { SettingsCard } from './components/SettingsCard';
import { DangerZoneCard } from './components/DangerZoneCard';
import { OnboardingFlow } from './components/OnboardingFlow';
import { PostCheckout } from './components/PostCheckout';
import { HabitDetail } from './constants';
import { motion, AnimatePresence } from 'motion/react';
import { useState, useEffect, ReactNode } from 'react';
import { Home, BarChart2, User, Loader2 } from 'lucide-react';
import { useAuth } from './hooks/useAuth';
import { useProfile } from './hooks/useProfile';
import { useRecommendations } from './hooks/useRecommendations';
import { useTranslation } from 'react-i18next';
import { identifyUser, trackEvent } from './lib/posthog';

type View = 'home' | 'tracking' | 'account';

export default function App() {
  const { t, i18n } = useTranslation();
  const { user, loading: authLoading, signOut } = useAuth();
  const { profile, isPro, refetch } = useProfile(user?.id);
  const { activeRecommendations, lockedRecommendation, loading: recsLoading, refetch: refetchRecommendations } = useRecommendations(user?.id, i18n.language);
  const [currentView, setCurrentView] = useState<View>('home');
  const [selectedHabit, setSelectedHabit] = useState<HabitDetail | null>(null);

  useEffect(() => {
    if (user?.id) identifyUser(user.id, { email: user.email });
  }, [user?.id, user?.email]);

  // Derive score from profile, fallback to sessionStorage onboarding data
  let displayScore = profile?.fertility_score ?? 50;
  if (!profile?.fertility_score) {
    try {
      const raw = sessionStorage.getItem('atilum_onboarding');
      if (raw) {
        const data = JSON.parse(raw);
        if (data.score) displayScore = data.score;
      }
    } catch { /* ignore */ }
  }

  // Show a minimal loading screen while Supabase checks the session
  if (authLoading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  // Post-checkout: show AuthGate for account creation
  if (window.location.pathname === '/post-checkout') {
    if (!user) {
      return <PostCheckout />;
    }
    // Already logged in → redirect to dashboard
    window.history.replaceState({}, '', '/');
  }

  // Not authenticated → show onboarding flow
  if (!user) {
    return <OnboardingFlow />;
  }

  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
    <div className="min-h-screen bg-stone-50 font-sans text-stone-900 pb-32 relative overflow-hidden selection:bg-emerald-200 selection:text-emerald-900">
      {/* Global CSS Noise Overlay */}
      <svg className="pointer-events-none fixed inset-0 z-50 h-full w-full opacity-[0.03] mix-blend-overlay">
        <filter id="noise">
          <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="4" stitchTiles="stitch" />
        </filter>
        <rect width="100%" height="100%" filter="url(#noise)" />
      </svg>

      {currentView !== 'account' && <FertilityBar score={displayScore} />}

      <main className="max-w-md mx-auto px-6 pt-10 relative z-10">
        <AnimatePresence mode="wait">
          {currentView === 'home' ? (
            <motion.div
              key="home"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.4, ease: [0.25, 1, 0.5, 1] }}
              className="flex flex-col gap-8"
            >
              <div className="flex flex-col gap-2">
                <h1 className="text-4xl font-extrabold tracking-tighter text-stone-900">{t('home.title')}</h1>
                <p className="text-[1.05rem] font-medium text-stone-500 tracking-tight">{t('home.subtitle')}</p>
              </div>

              <div className="flex flex-col gap-5">
                {recsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
                  </div>
                ) : activeRecommendations.length > 0 ? (
                  <>
                    {activeRecommendations.map((ur) => {
                      const rec = ur.recommendation;
                      return (
                        <div key={ur.id}>
                          <HabitCard
                            title={rec.title}
                            category={rec.category}
                            gain={rec.score_impact}
                            gainType={rec.impact_type}
                            onClick={() => { trackEvent('habit_opened', { habit: rec.slug, category: rec.category }); setSelectedHabit({
                              id: rec.slug,
                              title: rec.title,
                              category: rec.category,
                              gain: rec.score_impact,
                              description: rec.description,
                              actions: rec.actions,
                              scientificInsight: rec.insight,
                            }); }}
                          />
                        </div>
                      );
                    })}
                    {lockedRecommendation && (
                      <LockedHabitCard
                        title={lockedRecommendation.recommendation.title}
                        category={lockedRecommendation.recommendation.category}
                        gain={lockedRecommendation.recommendation.score_impact}
                        gainType={lockedRecommendation.recommendation.impact_type}
                      />
                    )}
                  </>
                ) : (
                  <p className="text-center text-stone-400 font-medium py-8">{t('home.noRecommendations')}</p>
                )}
              </div>
            </motion.div>
          ) : currentView === 'tracking' ? (
            <motion.div
              key="tracking"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.4, ease: [0.25, 1, 0.5, 1] }}
              className="flex flex-col gap-8"
            >
              <div className="flex flex-col gap-2">
                <h1 className="text-4xl font-extrabold tracking-tighter text-stone-900">{t('tracking.title')}</h1>
                <p className="text-[1.05rem] font-medium text-stone-500 tracking-tight">{t('tracking.subtitle')}</p>
              </div>

              <ProgressChart userId={user.id} fertilityScore={profile?.fertility_score} />

              <DailyQuestionCard userId={user.id} onScoreUpdate={() => refetch()} onCardUnlocked={() => refetchRecommendations()} />
            </motion.div>
          ) : (
            <motion.div
              key="account"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.4, ease: [0.25, 1, 0.5, 1] }}
              className="flex flex-col gap-8"
            >
              <div className="flex flex-col gap-2 text-center mb-2">
                <h1 className="text-4xl font-extrabold tracking-tighter text-stone-900">{t('account.title')}</h1>
                <p className="text-[1.05rem] font-medium text-stone-500 tracking-tight">{t('account.subtitle')}</p>
              </div>

              <AccountCard
                email={profile?.email ?? user.email ?? ''}
                onSignOut={signOut}
              />
              <SettingsCard />
              <DangerZoneCard />
              <SubscriptionCard
                isPro={isPro}
                proUntil={profile?.pro_until ?? null}
                userId={user.id}
                userEmail={profile?.email ?? user.email ?? undefined}
                paddleSubscriptionId={profile?.paddle_subscription_id}
              />

              <p className="text-[11px] font-medium text-stone-400 text-center px-4 leading-relaxed">
                {t('legal.medicalDisclaimer')}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Habit Detail Modal */}
      <HabitDetailModal
        habit={selectedHabit}
        onClose={() => setSelectedHabit(null)}
      />

      {/* Bottom Navigation */}
      <nav className="fixed bottom-8 left-1/2 -translate-x-1/2 w-[90%] max-w-md bg-white/80 backdrop-blur-2xl border border-stone-200/50 rounded-[2.5rem] p-2 shadow-[0_20px_50px_rgba(0,0,0,0.1)] z-50">
        <div className="flex items-center justify-around">
          <NavButton
            active={currentView === 'home'}
            onClick={() => { setCurrentView('home'); trackEvent('page_viewed', { page: 'home' }); }}
            icon={<Home className="w-6 h-6" />}
            label={t('nav.home')}
          />
          <NavButton
            active={currentView === 'tracking'}
            onClick={() => { setCurrentView('tracking'); trackEvent('page_viewed', { page: 'tracking' }); }}
            icon={<BarChart2 className="w-6 h-6" />}
            label={t('nav.tracking')}
          />
          <NavButton
            active={currentView === 'account'}
            onClick={() => { setCurrentView('account'); trackEvent('page_viewed', { page: 'account' }); }}
            icon={<User className="w-6 h-6" />}
            label={t('nav.account')}
          />
        </div>
      </nav>
    </div>
    </Sentry.ErrorBoundary>
  );
}

function ErrorFallback() {
  return (
    <div className="min-h-screen bg-stone-50 flex flex-col items-center justify-center gap-4 p-6 text-center">
      <h1 className="text-2xl font-bold text-stone-900">Something went wrong</h1>
      <p className="text-stone-500">An unexpected error occurred.</p>
      <button
        onClick={() => window.location.reload()}
        className="px-6 py-3 bg-emerald-600 text-white font-semibold rounded-2xl hover:bg-emerald-700 transition-colors"
      >
        Reload
      </button>
    </div>
  );
}

function NavButton({ active, onClick, icon, label }: { active: boolean, onClick: () => void, icon: ReactNode, label: string }) {
  return (
    <button
      onClick={onClick}
      className={`
        flex flex-col items-center gap-1 px-5 py-3 rounded-[2rem] transition-all duration-300 relative
        ${active ? 'text-emerald-600' : 'text-stone-400 hover:text-stone-600'}
      `}
    >
      {active && (
        <motion.div
          layoutId="nav-pill"
          className="absolute inset-0 bg-emerald-50 rounded-[2rem] -z-10"
          transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
        />
      )}
      {icon}
      <span className="text-[10px] font-bold uppercase tracking-widest">{label}</span>
    </button>
  );
}
